import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button, Badge } from '../components/ui';
import {
  MagnifyingGlassIcon,
  CreditCardIcon,
  ClockIcon,
  BookmarkIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { useRecentSearches } from '../hooks/api/usePermits';
import { useBillingBalance } from '../hooks/api/useBilling';

// Helper function to format date as "X time ago"
function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  return date.toLocaleDateString();
}

export default function DashboardPage() {
  const { data: recentSearches, isLoading: isLoadingSearches } = useRecentSearches(4);
  const { data: balance, isLoading: isLoadingBalance } = useBillingBalance();

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          Welcome back! 👋
        </h1>
        <p className="text-gray-600">Here's an overview of your account activity</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card variant="elevated" padding="lg" className="border-l-4 border-primary">
          <CardContent>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">Recent Searches</h3>
                {isLoadingSearches ? (
                  <p className="text-3xl font-bold text-gray-900">...</p>
                ) : (
                  <>
                    <p className="text-3xl font-bold text-gray-900">
                      {recentSearches?.length || 0}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      {recentSearches && recentSearches.length > 0
                        ? 'Last 4 searches'
                        : 'No searches yet'}
                    </p>
                  </>
                )}
              </div>
              <ClockIcon className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card variant="elevated" padding="lg" className="border-l-4 border-success">
          <CardContent>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">Saved Permits</h3>
                <p className="text-3xl font-bold text-gray-900">0</p>
                <p className="text-sm text-gray-500 mt-2">No saved permits</p>
              </div>
              <BookmarkIcon className="h-8 w-8 text-success" />
            </div>
          </CardContent>
        </Card>

        <Card variant="elevated" padding="lg" className="border-l-4 border-warning">
          <CardContent>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">Account Balance</h3>
                {isLoadingBalance ? (
                  <p className="text-3xl font-bold text-gray-900">...</p>
                ) : (
                  <>
                    <p className="text-3xl font-bold text-gray-900">
                      {Number(balance?.credit_balance ?? balance?.current_balance ?? 0).toFixed(1)} credits
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      <Link to="/account" className="text-primary hover:underline">
                        Manage credits
                      </Link>
                    </p>
                  </>
                )}
              </div>
              <CurrencyDollarIcon className="h-8 w-8 text-warning" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card variant="elevated" padding="lg" className="mb-8">
        <CardHeader className="mb-6">
          <CardTitle as="h2">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to="/permits/search">
              <Card
                variant="interactive"
                padding="md"
                className="h-full transition-all hover:scale-[1.02]"
              >
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <MagnifyingGlassIcon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">New Permit Search</h3>
                      <p className="text-sm text-gray-600">Find permit requirements and fees</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/account">
              <Card
                variant="interactive"
                padding="md"
                className="h-full transition-all hover:scale-[1.02]"
              >
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                      <CreditCardIcon className="h-6 w-6 text-success" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">Add Credits</h3>
                      <p className="text-sm text-gray-600">Manage your account balance</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Recent Searches Section */}
      {recentSearches && recentSearches.length > 0 && (
        <Card variant="elevated" padding="lg" className="mb-8">
          <CardHeader className="mb-6">
            <div className="flex items-center justify-between">
              <CardTitle as="h2">Recent Searches</CardTitle>
              <Link to="/permits/history">
                <Button variant="ghost" size="sm">
                  View All
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recentSearches.map((search) => (
                <Card key={search.id} variant="default" padding="md">
                  <CardContent>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {search.city}, {search.state}
                        </h3>
                        {search.municipality_name && (
                          <p className="text-sm text-gray-600 mb-2">
                            {search.municipality_name}
                          </p>
                        )}
                        <p className="text-sm text-gray-700 line-clamp-2 mb-2">
                          {search.job_description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <div>
                        {search.total_estimated_fee !== undefined && (
                          <p className="text-lg font-bold text-primary">
                            ${search.total_estimated_fee.toFixed(2)}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          {formatTimeAgo(search.created_at)}
                        </p>
                      </div>
                      <Link to={`/permits/${search.id}`}>
                        <Button variant="ghost" size="sm">
                          View
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Getting Started Section */}
      {(!recentSearches || recentSearches.length === 0) && (
        <Card variant="default" padding="lg" className="bg-gradient-to-br from-primary/5 to-teal/5">
          <CardContent>
            <div className="text-center max-w-2xl mx-auto">
              <Badge variant="primary" size="md" className="mb-4">
                Getting Started
              </Badge>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Ready to Calculate Your First Permit Fee?
              </h2>
              <p className="text-gray-600 mb-6">
                Start by searching for a permit. Enter your location and job details to get instant fee calculations.
              </p>
              <Link to="/permits/search">
                <Button variant="primary" size="lg">
                  Start Your First Search
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
